import { ApolloClient, InMemoryCache, from } from '@apollo/client';

import { APP_NAME } from '@/core/constants/app';

import introspectionResult from '../generated/fragmentMatcher';

import { mergeSortedByPrimary } from './_utils/mergeSortedByPrimary';
import { authLink } from './links/authLink';
import { errorLink } from './links/errorLink';
import { httpLink } from './links/httpLink';
import { retryLink } from './links/retryLink';
import { sentryLink } from './links/sentryLink';

const createApolloClient = () => {
	const link = from([
		sentryLink, // 1️. Logs all GraphQL errors before they are handled
		errorLink, // 2️. Handles GraphQL/network errors, can modify context (e.g., INVALID_TOKEN)
		authLink, // 3. Attaches the final authentication token
		retryLink, // 4. Decides if token refresh is needed based on error context from errorLink
		httpLink // 5️. Sends the request over the network
	]);

	const client = new ApolloClient({
		link,
		cache: new InMemoryCache({
			resultCacheMaxSize: 100000,
			possibleTypes: introspectionResult.possibleTypes,
			typePolicies: {
				Country: { keyFields: false },
				State: { keyFields: false },
				SpecialtyGroup: { keyFields: false },
				Specialty: { keyFields: false },
				HCPType: { keyFields: false },
				HCOType: { keyFields: false },
				// Note(Filip): `Reference` - As we query all picklist under a single query, we have to disable the caching as some entities have the same IDs (e.g. Doctor and Dead both have ID = "D"), which is fine because they are stored in separate tables, but causes an issue to Apollo when queried together.
				Reference: { keyFields: false },

				HCP: {
					keyFields: ['vid_V'],
					fields: {
						addresses: {
							merge: mergeSortedByPrimary('primaryCda_V')
						}
					}
				},

				HCO: {
					keyFields: ['vid_V'],
					fields: {
						addresses: {
							merge: mergeSortedByPrimary('primaryCda_V')
						}
					}
				},

				Query: {
					fields: {
						hcpSearchAndFilters: {
							keyArgs: ['$payload'],
							merge: (existing, incoming) => ({ ...existing, ...incoming })
						},
						hcoSearchAndFilters: {
							keyArgs: ['$input'],
							merge: (existing, incoming) => ({ ...existing, ...incoming })
						}
					}
				}
			}
		}),
		connectToDevTools: import.meta.env.MODE === 'development',
		name: APP_NAME,
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' }
	});

	return client;
};

export const client = createApolloClient();
